import ActionIconButton from '@components/Common/ActionIconButton';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import ErrorMessage from '@components/Common/ErrorMessage';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import StatTile from '@components/Common/StatTile';
import { useToast } from '@components/Common/Toast/ToastProvider';
import {
  adminAmbassadorCertificatesQuery,
  adminAmbassadorRewardsQuery,
  adminAmbassadorSummaryQuery,
  getAmbassadorByIdQuery,
  markReferralRewardsPaidMutation,
  setAmbassadorStatusMutation,
} from '@constants/graphQLQueries';
import { formatDate, formatDateTime, formatRelative } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { humanizeEnum } from '@helpers/labels';
import { lastListView } from '@helpers/listView';
import { fmtMoney } from '@helpers/money';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { REWARD_STATUS_LABELS } from '../Referrals/review';
import {
  ambassadorName,
  badgeLabel,
  FUNNEL_STEPS,
  initialsOf,
  isSuspended,
  nextBadgeText,
  payoutText,
  REWARD_STATUS_TONE,
  rewardTitleOf,
  type Ambassador,
  type AmbassadorSummary,
  type Certificate,
  type RewardRow,
} from './ambassador';
import { CandidatesPanel, InvitesPanel, RewardsTable } from './panels';
import './index.scss';

const TABS: WorkspaceTab[] = [
  { id: 'overview', label: 'Overview', icon: <InfoOutlinedIcon /> },
  { id: 'candidates', label: 'Candidates', icon: <Groups2OutlinedIcon /> },
  { id: 'invites', label: 'Invites', icon: <MarkEmailReadOutlinedIcon /> },
  { id: 'rewards', label: 'Rewards', icon: <PaymentsOutlinedIcon /> },
  { id: 'profile', label: 'Profile', icon: <PersonOutlineOutlinedIcon /> },
];

/** The whole record a workspace opens on. Summary, certificates and rewards may each fail alone. */
interface Loaded {
  ambassador: Ambassador;
  summary: AmbassadorSummary | null;
  certificates: Certificate[] | null;
  rewards: RewardRow[] | null;
}

const settled = <T,>(r: PromiseSettledResult<any>, pick: (d: any) => T): T | null =>
  r.status === 'fulfilled' ? pick(r.value?.data) ?? null : null;

const errorText = (e: any, fallback: string) =>
  String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || fallback;

/**
 * One HR Ambassador, as an entity workspace. Super Admin only (route guard, BFF and backend).
 *
 * Suspend and Reinstate, and marking an approved reward paid; otherwise what they earned and where
 * their candidates are, as their own dashboard shows it, so a Super Admin answering a question sees
 * what the ambassador sees. Approving rewards stays on Referrals, Payouts, where the evidence is;
 * a reward under review links there.
 */
const AmbassadorWorkspace: React.FC = () => {
  const { ambassadorId = '' } = useParams<{ ambassadorId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'overview');
  const [activityOpen, toggleActivity] = useActivityRail();

  const [record, setRecord] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  /** The approved reward the payment-reference dialog is open for. */
  const [paying, setPaying] = useState<RewardRow | null>(null);
  const [payBusy, setPayBusy] = useState(false);

  /* Which ambassador the latest load is for, so a slow response cannot land under another's URL. */
  const loadFor = useRef(ambassadorId);
  const load = useCallback(async () => {
    loadFor.current = ambassadorId;
    setLoading(true);
    setError(null);
    try {
      const [a, s, c, r] = await Promise.allSettled([
        fetchGraphQLApiData(getAmbassadorByIdQuery(ambassadorId)),
        fetchGraphQLApiData(adminAmbassadorSummaryQuery(ambassadorId)),
        fetchGraphQLApiData(adminAmbassadorCertificatesQuery(ambassadorId)),
        fetchGraphQLApiData(adminAmbassadorRewardsQuery(ambassadorId)),
      ]);
      if (loadFor.current !== ambassadorId) return;
      if (a.status === 'rejected') throw a.reason;
      const ambassador: Ambassador | null = a.value?.data?.getAmbassadorById ?? null;
      if (!ambassador) {
        setRecord(null);
        setError('This HR Ambassador could not be found.');
        return;
      }
      setRecord({
        ambassador,
        summary: settled(s, (d) => d?.adminAmbassadorSummary),
        certificates: settled(c, (d) => (d?.adminAmbassadorCertificates ?? []).filter(Boolean)),
        rewards: settled(r, (d) => (d?.adminAmbassadorRewards ?? []).filter(Boolean)),
      });
    } catch {
      if (loadFor.current === ambassadorId) setError('Failed to load the HR Ambassador. Please try again.');
    } finally {
      if (loadFor.current === ambassadorId) setLoading(false);
    }
  }, [ambassadorId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !record) return <SkeletonLoader variant="workspace" />;
  if (error || !record) {
    return <ErrorMessage page message={error ?? 'This HR Ambassador could not be found.'} onRetry={load} />;
  }

  const { ambassador: a, summary, certificates, rewards } = record;
  const name = ambassadorName(a);
  const suspended = isSuspended(a);
  const pd = a.personalDetails;
  const currency = summary?.currency ?? 'INR';

  const changeStatus = async (reason?: string) => {
    setBusy(true);
    try {
      const r = await fetchGraphQLApiData(setAmbassadorStatusMutation(a._id, suspended ? 'ACTIVE' : 'SUSPENDED', reason ?? ''));
      const next: Ambassador | null = r?.data?.setAmbassadorStatus ?? null;
      setRecord((prev) => (prev ? { ...prev, ambassador: next ?? { ...prev.ambassador, status: suspended ? 'ACTIVE' : 'SUSPENDED' } } : prev));
      showToast(suspended ? 'Reinstated' : 'Suspended', 'success');
      setConfirming(false);
    } catch (e) {
      showToast(errorText(e, 'Not saved.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  /* The same dialog and mutation as Referrals, Payouts, for one reward. Reloading the record also
     refreshes the Paid tile, which a patch of the one row would leave stale. */
  const markPaid = async (reference?: string) => {
    if (!paying) return;
    setPayBusy(true);
    try {
      await fetchGraphQLApiData(markReferralRewardsPaidMutation([paying.id], reference ?? ''));
      showToast('Marked paid', 'success');
      setPaying(null);
      load();
    } catch (e) {
      showToast(errorText(e, 'Not marked paid.'), 'error');
    } finally {
      setPayBusy(false);
    }
  };

  const glance: GlanceRow[] = [
    { key: 'Code', value: a.code || '-', mono: true },
    { key: 'City', value: pd?.city || '-' },
    { key: 'Firm', value: a.firm || '-' },
    { key: 'Credits', value: summary ? String(summary.counted ?? 0) : '-', mono: true },
    { key: 'Joined', value: formatDate(a.createdAt), mono: true },
    { key: 'Last login', value: formatRelative(a.lastLoginAt), mono: true },
  ];

  const identityRail = (
    <IdentityRail
      initials={initialsOf(name)}
      name={name}
      subId={a.code ?? a._id}
      statusChip={a.status ? <CRMStatusChip status={a.status.toLowerCase()} /> : undefined}
      badges={summary?.badge ? <CRMStatusChip status="approved" label={badgeLabel(summary.badge)} /> : undefined}
      secondaryActions={
        <ActionIconButton
          label={suspended ? 'Reinstate this ambassador' : 'Suspend this ambassador'}
          icon={suspended ? <RestoreOutlinedIcon fontSize="small" /> : <BlockOutlinedIcon fontSize="small" />}
          color={suspended ? 'primary' : 'error'}
          busy={busy}
          onClick={() => setConfirming(true)}
        />
      }
      glance={glance}
    />
  );

  /* ── Overview ─────────────────────────────────────────────────────────── */

  const counted = summary?.counted ?? 0;
  const numbers = (
    <SectionCard
      title="Numbers"
      icon={<PaymentsOutlinedIcon />}
      meta={summary ? nextBadgeText(summary) ?? badgeLabel(summary.badge) : undefined}
    >
      <Box className="amb-wide">
        {summary ? (
          <Box className="cc-stat-grid">
            <StatTile label="Credits" value={counted} sublabel={`${summary.qualified ?? 0} qualified`} />
            <StatTile label="Earned" value={fmtMoney(summary.earned ?? 0, currency)} sublabel="Approved or paid" />
            <StatTile label="Paid" value={fmtMoney(summary.paid ?? 0, currency)} />
            <StatTile
              label="Being checked"
              value={fmtMoney(summary.pendingAmount ?? 0, currency)}
              sublabel={`${summary.underReview ?? 0} ${(summary.underReview ?? 0) === 1 ? 'reward' : 'rewards'} under review`}
              tone={(summary.underReview ?? 0) > 0 ? 'warning' : 'neutral'}
            />
          </Box>
        ) : (
          <EmptyState compact title="Numbers could not be loaded" body="Try again in a moment." />
        )}
      </Box>
    </SectionCard>
  );

  const tiers = summary?.tiers ?? [];
  const milestones = (
    <SectionCard
      title="Milestones"
      icon={<EmojiEventsOutlinedIcon />}
      meta={tiers.length ? `${tiers.filter((t) => t.reached).length} of ${tiers.length} reached` : undefined}
    >
      <Box className="amb-wide">
        {tiers.length === 0 ? (
          <EmptyState compact title="No milestones set" body="Milestones apply once the programme runs on milestones (Settings)." />
        ) : (
          <Box component="ol" className="amb-steps" aria-label="Milestone track">
            {tiers.map((t) => (
              <Box component="li" key={t.threshold} className={`amb-step${t.reached ? ' is-reached' : ''}`}>
                <span className="amb-step__label">{`${t.threshold} credits`}</span>
                <span className="amb-step__value">{fmtMoney(t.amount, currency)}</span>
                {t.reached && t.status ? (
                  <CRMStatusChip status={REWARD_STATUS_TONE[t.status] ?? 'inactive'} label={REWARD_STATUS_LABELS[t.status] ?? humanizeEnum(t.status)} />
                ) : (
                  <span className="amb-step__meta">
                    {t.reached ? 'Reached' : `${Math.max(0, t.threshold - counted)} to go`}
                  </span>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </SectionCard>
  );

  const funnel = summary?.funnel;
  const funnelCard = (
    <SectionCard title="Funnel" icon={<FilterAltOutlinedIcon />} meta={funnel ? `${funnel.qualified ?? 0} qualified` : undefined}>
      <Box className="amb-wide">
        {funnel ? (
          <Box component="ol" className="amb-steps" aria-label="Funnel">
            {FUNNEL_STEPS.map((s) => (
              <Box component="li" key={s.key} className="amb-step">
                <span className="amb-step__label">{s.label}</span>
                <span className="amb-step__value">{(funnel[s.key] ?? 0).toLocaleString('en-IN')}</span>
              </Box>
            ))}
          </Box>
        ) : (
          <EmptyState compact title="No funnel yet" body="Counts appear once someone opens their link." />
        )}
      </Box>
    </SectionCard>
  );

  const certificatesCard = (
    <SectionCard title="Certificates" icon={<VerifiedOutlinedIcon />} meta={certificates?.length ? String(certificates.length) : undefined}>
      {certificates === null ? (
        <Box className="amb-wide"><EmptyState compact title="Certificates could not be loaded" body="Try again in a moment." /></Box>
      ) : certificates.length === 0 ? (
        <Box className="amb-wide"><EmptyState compact title="No certificates yet" body="One is issued at each recognition level: 25, 50 and 100 credits." /></Box>
      ) : (
        certificates.map((c) => (
          <WorkspaceField
            key={c._id}
            label={badgeLabel(c.level)}
            value={(
              <span className="amb-chips">
                <span>{`Issued ${formatDate(c.issuedAt)}`}</span>
                <CRMStatusChip status={c.active ? 'active' : 'inactive'} label={c.active ? 'Valid' : 'Withdrawn'} />
              </span>
            )}
          />
        ))
      )}
    </SectionCard>
  );

  const payoutCard = (
    <SectionCard title="Payout" icon={<PaymentsOutlinedIcon />} meta={summary && !payoutText(summary) ? 'Not given' : undefined}>
      <WorkspaceField label="UPI ID" mono value={summary?.payoutUpiId} />
      <WorkspaceField label="Phone" mono value={summary?.payoutPhone} />
    </SectionCard>
  );

  const overview = (
    <>
      {numbers}
      {milestones}
      {funnelCard}
      {certificatesCard}
      {payoutCard}
    </>
  );

  /* ── Other tabs ───────────────────────────────────────────────────────── */

  const candidates = (
    <SectionCard title="Candidates" icon={<Groups2OutlinedIcon />} meta={summary ? `${summary.qualified ?? 0} qualified` : undefined}>
      <CandidatesPanel ambassadorId={a._id} />
    </SectionCard>
  );

  const invites = (
    <SectionCard title="Invites" icon={<MarkEmailReadOutlinedIcon />}>
      <InvitesPanel ambassadorId={a._id} />
    </SectionCard>
  );

  const rewardsCard = (
    <SectionCard title="Rewards" icon={<PaymentsOutlinedIcon />} meta={rewards?.length ? String(rewards.length) : undefined}>
      {rewards === null ? (
        <Box className="amb-wide"><EmptyState compact title="Rewards could not be loaded" body="Try again in a moment." /></Box>
      ) : (
        <RewardsTable rows={rewards} ambassadorId={a._id} onPay={setPaying} />
      )}
    </SectionCard>
  );

  const profile = (
    <>
      <SectionCard title="Contact" icon={<ContactMailOutlinedIcon />}>
        <WorkspaceField label="First name" value={pd?.firstName} />
        <WorkspaceField label="Last name" value={pd?.lastName} />
        <WorkspaceField
          label="Email"
          value={pd?.email ? <a className="cc-link" href={`mailto:${pd.email}`}>{pd.email}</a> : null}
        />
        <WorkspaceField label="Phone" mono value={pd?.phone ? <a className="cc-link" href={`tel:${pd.phone}`}>{pd.phone}</a> : null} />
        <WorkspaceField label="City" value={pd?.city} />
        <WorkspaceField label="Firm" value={a.firm} />
      </SectionCard>
      <SectionCard title="Account" icon={<ManageAccountsOutlinedIcon />}>
        <WorkspaceField label="Status" value={a.status ? <CRMStatusChip status={a.status.toLowerCase()} /> : null} />
        {a.statusReason && <WorkspaceField label="Status reason" full value={a.statusReason} />}
        <WorkspaceField label="Signed up" value={formatDateTime(a.createdAt)} />
        <WorkspaceField label="Signs in with" value={a.authProvider ? humanizeEnum(a.authProvider) : null} />
        <WorkspaceField label="Referral code" mono value={a.code} />
        <WorkspaceField label="Code renamed" value={a.codeRenamedAt ? formatDateTime(a.codeRenamedAt) : 'Not renamed'} />
        <WorkspaceField label="Last login" value={a.lastLoginAt ? formatDateTime(a.lastLoginAt) : 'Never'} />
        <WorkspaceField label="Ambassador ID" mono value={a._id} />
      </SectionCard>
    </>
  );

  const tabContent: Record<string, React.ReactNode> = {
    overview,
    candidates,
    invites,
    rewards: rewardsCard,
    profile,
  };

  const timeline: TimelineEntry[] = [
    a.createdAt && { id: 'joined', kind: 'submitted' as const, text: 'Registered as an HR Ambassador', meta: formatDateTime(a.createdAt) },
    a.codeRenamedAt && { id: 'renamed', kind: 'status' as const, text: `Renamed their code to ${a.code}`, meta: formatDateTime(a.codeRenamedAt) },
    a.lastLoginAt && { id: 'login', kind: 'status' as const, text: 'Last signed in', meta: formatDateTime(a.lastLoginAt) },
    suspended && { id: 'suspended', kind: 'status' as const, text: 'Suspended', body: a.statusReason || undefined },
  ].filter(Boolean) as TimelineEntry[];

  return (
    <>
      <EntityWorkspace
        backLabel="HR Ambassadors"
        onBack={() => navigate(lastListView('/ambassadors'))}
        identityRail={identityRail}
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        activityRail={<ActivityRail timeline={timeline} />}
        activityOpen={activityOpen}
        onToggleActivity={toggleActivity}
      >
        {tabContent[activeTab]}
      </EntityWorkspace>
      <ConfirmDialog
        open={confirming && !suspended}
        title={`Suspend ${name}?`}
        body="Their code stops attributing new sign-ups, and their certificates' verification page says they are no longer valid. A reason is required."
        reasonLabel="Reason (kept on their record)"
        reasonPlaceholder="For example: referrals from outside our industries"
        actionLabel="Suspend"
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={(reason) => changeStatus(reason)}
      />
      <ConfirmDialog
        open={confirming && suspended}
        title={`Reinstate ${name}?`}
        body="Their code attributes new sign-ups again."
        actionLabel="Reinstate"
        actionColor="primary"
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => changeStatus()}
      />
      <ConfirmDialog
        open={!!paying}
        title="Mark this reward paid?"
        body={
          paying
            ? `${fmtMoney(paying.amount ?? 0, paying.currency ?? currency)} to ${name} for ${rewardTitleOf(paying)}${payoutText(summary) ? ` (${payoutText(summary)})` : ', who has not given payout details'}. Enter the UPI or bank reference of the payment you made.`
            : ''
        }
        reasonLabel="Payment reference"
        reasonPlaceholder="UPI or bank transaction id"
        actionLabel="Mark paid"
        actionColor="primary"
        busy={payBusy}
        onCancel={() => setPaying(null)}
        onConfirm={markPaid}
      />
    </>
  );
};

export default AmbassadorWorkspace;
